import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Client } from "@twilio/conversations";
import Swal from "sweetalert2";
import { getUnreadMessagesFromAPI, getTokenFromAPI } from "../store/chats/thunks";
import { markConversationAsUnread, updateChatWithNewMessage } from "../store/chats/chatsSlice";
import { getToken, getMessaging, isSupported } from "firebase/messaging";
import { adminAPI } from "../api/adminAPI";

export const useChatNotifications = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { tokenChat } = useSelector((state) => state.chats);
  const { email, jwt } = useSelector((state) => state.auth);
  const clientRef = useRef(null);

  // Setup Firebase Cloud Messaging for Background/OS level notifications
  useEffect(() => {
    if (!jwt) return;

    const setupFCM = async () => {
      try {
        if (!("Notification" in window)) {
          console.warn("This browser does not support desktop notifications.");
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.warn("Notification permission was not granted.");
          return;
        }

        const supported = await isSupported();
        if (!supported) {
          console.warn("Firebase Messaging no está soportado en este entorno/navegador (puede deberse a falta de HTTPS).");
          return;
        }

        const messagingInstance = getMessaging();

        const firebaseConfig = {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID,
        };
        const serializedConfig = encodeURIComponent(JSON.stringify(firebaseConfig));

        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.register(
            `/firebase-messaging-sw.js?config=${serializedConfig}`
          );

          const currentToken = await getToken(messagingInstance, {
            vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: registration,
          });

          if (currentToken) {
            await adminAPI.put(
              "/auth/update-admin-fcm-token",
              { fcmToken: currentToken },
              {
                headers: {
                  "x-token": jwt,
                },
              }
            );
            console.log("FCM Token registered and updated on backend successfully.");
          }
        }
      } catch (err) {
        console.error("Error setting up FCM in useChatNotifications hook:", err);
      }
    };

    setupFCM();
  }, [jwt]);


  useEffect(() => {
    if (!tokenChat) {
      dispatch(getTokenFromAPI());
    }
  }, [dispatch, tokenChat]);

  const playNotificationSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(587.33, audioCtx.currentTime); // D5 note
      gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);

      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.3);
    } catch (e) {
      console.error("Audio playback error:", e);
    }
  };

  useEffect(() => {
    if (!tokenChat || !email) return;

    let activeClient = null;

    const initClient = async () => {
      try {
        const client = new Client(tokenChat);
        clientRef.current = client;
        activeClient = client;

        client.on("connectionStateChanged", (state) => {
          if (state === "connected") {
            client.on("messageAdded", async (message) => {
              if (message.author === email) return;

              // Don't show toast if the admin is actively in this conversation's chat page
              const pathParts = location.pathname.split("/");
              const activeConversationSid = pathParts[4];
              if (activeConversationSid === message.conversation.sid) return;

              playNotificationSound();

              const messageText = message.body ? message.body : "Nuevo mensaje de archivo o multimedia";
              const truncatedText = messageText.length > 40 ? `${messageText.substring(0, 40)}...` : messageText;

              dispatch(getUnreadMessagesFromAPI());

              dispatch(updateChatWithNewMessage({
                conversationSid: message.conversation.sid,
                body: message.body,
                author: message.author,
                date: message.dateCreated ? message.dateCreated.toISOString() : new Date().toISOString(),
              }));

              // Always mark conversation as unread to trigger row highlight
              dispatch(markConversationAsUnread(message.conversation.sid));

              if (document.visibilityState !== "visible") {
                if (Notification.permission === "granted") {
                  try {
                    new Notification("Nuevo mensaje de chat", {
                      body: `${message.author}: ${truncatedText}`,
                      icon: "/favicon.ico"
                    });
                  } catch (e) {
                    console.error("Error displaying native notification:", e);
                  }
                }
              }

              const Toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 4500,
                timerProgressBar: true,
                didOpen: (toast) => {
                  toast.addEventListener("mouseenter", Swal.stopTimer);
                  toast.addEventListener("mouseleave", Swal.resumeTimer);
                }
              });

              Toast.fire({
                icon: "info",
                title: "Nuevo mensaje de chat",
                text: `"${truncatedText}"`,
              });
            });
          }
        });
      } catch (err) {
        console.error("Error initializing Twilio client in useChatNotifications hook:", err);
      }
    };

    initClient();

    return () => {
      if (activeClient) {
        activeClient.shutdown();
        if (clientRef.current === activeClient) {
          clientRef.current = null;
        }
      }
    };
  }, [tokenChat, email, location.pathname, dispatch]);
};
